import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Loader2, 
    Search,
    MapPin,
    ArrowRight,
    BarChart3,
    HelpCircle,
    FileText
} from 'lucide-react';
import { addressAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * Data Status Badge - Shows review count and summary availability
 * LEGAL COMPLIANCE: Does NOT display risk terminology
 * Summary available when at least 1 review exists
 */
const DataStatusBadge = ({ reviewCount = 0, size = 'small' }) => {
    const hasData = reviewCount >= 1;
    
    const sizeClasses = {
        small: 'px-2 py-0.5 text-xs',
        default: 'px-2.5 py-1 text-xs'
    };
    
    if (hasData) {
        return (
            <span className={cn(
                "inline-flex items-center gap-1 font-medium rounded-full bg-primary/20 text-primary",
                sizeClasses[size]
            )}>
                <BarChart3 className="h-3 w-3" />
                Summary
            </span>
        );
    }
    
    return (
        <span className={cn(
            "inline-flex items-center gap-1 font-medium rounded-full bg-muted/50 text-muted-foreground",
            sizeClasses[size]
        )}>
            <HelpCircle className="h-3 w-3" />
            No data
        </span>
    );
};

const SearchPage = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim() || searchQuery.length < 2) {
            toast.error('Please enter at least 2 characters to search');
            return;
        }

        setSearching(true);
        setSearchPerformed(true);

        try {
            const response = await addressAPI.search(searchQuery);
            setSearchResults(response.data);
        } catch (err) {
            toast.error('Search failed. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const handleAddNewAddress = async () => {
        // If we have a standardized address from Radar, use that
        if (selectedAddress) {
            try {
                const response = await addressAPI.create({
                    street_address: selectedAddress.street_address,
                    city: selectedAddress.city,
                    state: selectedAddress.state,
                    zip_code: selectedAddress.zip_code
                });
                navigate(`/property/${response.data.id}`);
            } catch (err) {
                toast.error('Failed to create address');
            }
            return;
        }

        // Fallback to parsing the manual input
        const parts = searchQuery.split(',').map(s => s.trim());
        const street = parts[0] || searchQuery;
        const city = parts[1] || '';

        if (!street || !city) {
            toast.error('Please enter address as: Street Address, City');
            return;
        }

        try {
            const response = await addressAPI.create({
                street_address: street,
                city: city
            });
            navigate(`/property/${response.data.id}`);
        } catch (err) {
            toast.error('Failed to create address');
        }
    };

    const handleAddressSelect = (standardizedAddress) => {
        setSelectedAddress(standardizedAddress);
        setSearchQuery(standardizedAddress.street_address);
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="font-heading font-extrabold text-3xl text-foreground mb-2">
                    Search Addresses
                </h1>
                <p className="text-muted-foreground mb-8">
                    Look up job addresses to view contractor-reported job experiences
                </p>

                {/* Search Form */}
                <Card className="bg-card border-border mb-8">
                    <CardContent className="p-6">
                        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <AddressAutocomplete
                                    value={searchQuery}
                                    onChange={(val) => {
                                        setSearchQuery(val);
                                        setSelectedAddress(null);
                                    }}
                                    onSelect={handleAddressSelect}
                                    data-testid="search-page-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="h-12 px-8 active:scale-95 transition-transform"
                                disabled={searching}
                                data-testid="search-page-btn"
                            >
                                {searching ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <>
                                        <Search className="h-5 w-5 mr-2" />
                                        Search
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Results */}
                {searchPerformed && (
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="font-heading font-bold text-xl text-foreground">
                                {searchResults.length > 0 
                                    ? `Found ${searchResults.length} address(es)` 
                                    : 'No Results Found'
                                }
                            </CardTitle>
                            {searchResults.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1" data-testid="search-disclaimer">
                                    Results show contractor-reported job experiences, not property ratings or assessments of occupants.
                                </p>
                            )}
                        </CardHeader>
                        <CardContent>
                            {searchResults.length > 0 ? (
                                <div className="space-y-3">
                                    {searchResults.map((address) => (
                                        <div
                                            key={address.id}
                                            onClick={() => navigate(`/property/${address.id}`)}
                                            className="flex items-center justify-between p-4 bg-accent/50 rounded-lg border border-border hover:border-muted-foreground transition-colors cursor-pointer"
                                            data-testid={`result-${address.id}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <MapPin className="h-5 w-5 text-primary" />
                                                <div>
                                                    <p className="font-medium text-foreground">
                                                        {address.display_address || address.street_address}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {address.city}{address.state && `, ${address.state}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className="text-sm text-muted-foreground hidden sm:block">
                                                    {address.active_review_count || address.review_count || 0} review{(address.active_review_count || address.review_count || 0) !== 1 ? 's' : ''}
                                                </span>
                                                <DataStatusBadge reviewCount={address.active_review_count || address.review_count || 0} size="small" />
                                                <ArrowRight className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground mb-4">
                                        No addresses found matching "{searchQuery}"
                                    </p>
                                    <Button
                                        onClick={handleAddNewAddress}
                                        variant="outline"
                                        data-testid="add-address-btn"
                                    >
                                        Review Property Now
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default SearchPage;
